import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { ReactEditorViewContext } from '@atlaskit/editor-common/ui-react';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { RemixReadyToast } from '@atlassian/malleable-ui-components/remix/remix-ready-toast';
import type {
	AddRemixHistoryEntryPayload,
	AddRemixHistoryStackEntryPayload,
} from '@atlassian/malleable-ui-components/types';

import { makeAddRemixHistoryEntryCommand } from '../../editor-commands/addRemixHistoryEntry';
import { makeAddRemixHistoryStackEntryCommand } from '../../editor-commands/addRemixHistoryStackEntry';
import { clearRemixThinkingDecoration } from '../../editor-commands/clear-remix-thinking-decoration';
import { setRemixThinkingComplete } from '../../editor-commands/set-remix-thinking-complete';
import { setShowLocationPickerEditorCommand } from '../../editor-commands/set-show-location-picker';
import { onStartRemixThinking } from '../../editor-commands/start-remix-thinking';
import type { MalleableUiPlugin, PluginConfiguration } from '../../malleableUiPluginType';
import { getRemixThinkingWidgetContainer } from '../../pm-plugins/remix-thinking/remix-thinking-decoration';
import { useIsThinkingWidgetInViewport } from '../hooks/useIsThinkingWidgetInViewport';
import {
	cancelRemixThinking,
	onRemixThinkingCompleteAnimationFinished,
} from '../utils/remix-state';

interface RemixContentProps {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	config: PluginConfiguration;
}

export const RemixContent = ({ api, config }: RemixContentProps): React.JSX.Element | null => {
	const { createRemixContentComponent } = config;
	const { editorView } = useContext(ReactEditorViewContext);

	const isViewMode = useSharedPluginStateWithSelector(
		api,
		['editorViewMode'],
		(states) => states.editorViewModeState?.mode === 'view',
	);

	const isModalOpen = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isRemixModalOpen || false,
	);

	const isThinking = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isThinking || false,
	);

	const isComplete = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isComplete || false,
	);

	const remixModalMetadata = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixModalMetadata,
	);

	const remixSource = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixSource,
	);

	const showLocationPicker = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.showLocationPicker || false,
	);

	const isUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isUpdate || false,
	);

	const historyEntryForUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.historyEntryForUpdate,
	);

	const dynamicUiSubtypeForUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.dynamicUiSubtypeForUpdate,
	);

	// Start Remix experience for floating toolbar entry point (can't use hooks in toolbar config).
	const experienceTracker = useAIMateExperienceTracker();
	const prevIsModalOpen = useRef(false);
	useEffect(() => {
		if (!prevIsModalOpen.current && isModalOpen && remixSource === 'floatingToolbar') {
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'floatingToolbar',
					product: 'confluence',
				},
			});
		}
		prevIsModalOpen.current = isModalOpen;
	}, [isModalOpen, remixSource, experienceTracker]);
	const Component = useMemo(() => createRemixContentComponent(), [createRemixContentComponent]);

	const handleModalClose = useCallback(() => {
		api?.core.actions.execute(api?.malleableUi.commands.closeRemixModal());
	}, [api]);

	const onStartThinking = useCallback(
		(options?: { contentType?: string }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				onStartRemixThinking({ api, options });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(true);
				api?.core.actions.execute(api?.malleableUi.commands.startRemixThinking(options));
			}
		},
		[api],
	);

	const onStopThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				cancelRemixThinking({ api, reopenModal: options?.reopenModal });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
				api?.core.actions.execute(api?.malleableUi.commands.stopRemixThinking(options));
			}
		},
		[api],
	);

	const clearThinkingDecoration = useCallback(() => {
		api?.core.actions.execute(clearRemixThinkingDecoration());
	}, [api]);

	const replaceWhiteboardEmbed = useCallback(
		(options: { contentId: string; finalizedUrl: string; localId?: string }) => {
			api?.core.actions.execute(api?.malleableUi.commands.replaceWhiteboardEmbed(options));
		},
		[api],
	);

	const setThinkingComplete = useCallback(
		(isComplete: boolean) => {
			// this is to clear the inline-Rovo streaming target when the remix is complete
			if (isComplete) {
				api?.aiExperience?.actions?.endInlineRovoStreaming?.();
			}
			api?.core.actions.execute(setRemixThinkingComplete(isComplete));
		},
		[api],
	);

	const finishRemixThinking = useCallback(() => {
		onRemixThinkingCompleteAnimationFinished({ api });
	}, [api]);

	const _onRemixThinkingCompleteAnimationFinished = useCallback(() => {
		onRemixThinkingCompleteAnimationFinished({ api });
	}, [api]);

	const setShowLocationPicker = useCallback(
		(showLocationPicker: boolean) => {
			api?.core.actions.execute(setShowLocationPickerEditorCommand(showLocationPicker));
		},
		[api],
	);

	const addHistoryEntry = useCallback(
		(options: AddRemixHistoryEntryPayload) => {
			api?.core.actions.execute(makeAddRemixHistoryEntryCommand(options));
		},
		[api],
	);

	const addHistoryStackEntry = useCallback(
		(options: AddRemixHistoryStackEntryPayload) => {
			api?.core.actions.execute(makeAddRemixHistoryStackEntryCommand(options));
		},
		[api],
	);

	// Bridges the plugin's `trackVariantUpdateComplete` command into the
	// remix flow; `useOnComplete` fires it once the FE insertion settles.
	const onVariantUpdateComplete = useCallback(
		(id: string) => {
			if (!api?.malleableUi?.commands.trackVariantUpdateComplete) {
				return;
			}
			api.core.actions.execute(
				api.malleableUi.commands.trackVariantUpdateComplete({ id }),
			);
		},
		[api],
	);

	const isThinkingWidgetInViewport = useIsThinkingWidgetInViewport({ editorView, isComplete });

	const handleReadyToastView = useCallback(() => {
		if (editorView) {
			const container = getRemixThinkingWidgetContainer(editorView);
			if (fg('platform_editor_ai_remix_toolbar')) {
				// for insertion, the next sibling should be the inserted content
				// but after insertion, the decoration is re-rendered after the insertion
				// so the inserted content become the previous sibling
				// for update, the previous sibling is the updated content
				const sibling = container?.previousElementSibling;
				sibling?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			} else {
				// the next sibling is the inserted content
				const nextSibling = container?.nextSibling;
				if (nextSibling instanceof HTMLElement) {
					nextSibling.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
			}
		}
		// setThinkingComplete(false); do the job, but a reset could be more meaningful
		setThinkingComplete(false);
	}, [editorView, setThinkingComplete]);

	const handleReadyToastClose = useCallback(() => {
		// setThinkingComplete(false); do the job, but a reset could be more meaningful
		setThinkingComplete(false);
	}, [setThinkingComplete]);

	// handle escape key to stop remix thinking
	useEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}

		if (!isThinking || !editorView) {
			return;
		}

		// focus editor first
		if (!editorView.hasFocus()) {
			editorView.focus();
		}

		return bind(editorView.dom, {
			type: 'keydown',
			listener: (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					e.stopPropagation();

					if (fg('platform_editor_ai_remix_toolbar')) {
						cancelRemixThinking({ api });
					} else {
						if (
							expValEqualsNoExposure(
								'cc_page_experiences_editor_image_generation',
								'isEnabled',
								true,
							)
						) {
							// If a pre-supplied userPrompt was cancelled (e.g. image generation flow),
							// call the onCancel callback instead of reopening the remix modal.
							const currentMetadata =
								api?.malleableUi?.sharedState.currentState()?.remixModalMetadata;
							const cancelledPrompt = currentMetadata?.userPrompt;
							const onCancel = currentMetadata?.onCancel;
							if (cancelledPrompt && onCancel) {
								onStopThinking({ reopenModal: false });
								onCancel();
								return;
							}
						}
						onStopThinking({
							reopenModal: fg('platform_editor_ai_remix_toolbar') ? !isUpdate : true,
						});
					}
				}
			},
		});
	}, [isThinking, onStopThinking, editorView, api?.malleableUi.sharedState, isUpdate, api]);

	// When the remix modal overlay is open (isModalOpen),
	// set the editor user intent to 'overlayOpen' so the floating toolbar is suppressed
	// (it would otherwise appear above the overlay in Jira).
	// We intentionally do NOT suppress during the thinking-only state
	// — the user may still interact with the editor while the AI is thinking.
	// Reset to 'default' when the modal closes or the component unmounts.
	useLayoutEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}
		if (!isModalOpen) {
			if (api?.userIntent?.sharedState.currentState()?.currentUserIntent === 'overlayOpen') {
				api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('default'));
			}
			return;
		}
		api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('overlayOpen'));
		return () => {
			api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('default'));
		};
	}, [isModalOpen, api]);

	if (!api || !Component) {
		return null;
	}

	const selectedContext = api?.aiExperience?.actions.getAISelectionContext();
	if (!selectedContext) {
		return null;
	}

	// when the remix isComplete and
	// - the modal is not open because isComplete may not be reset to false when the modal is open
	// - not thinking because we need to show the thinking bar when isThinking and isComplete
	//   - after onCompleteAnimationFinished, isThinking will be false and only isComplete will be true
	// we need to show the ready toast
	if (
		!isViewMode &&
		isComplete &&
		!isThinking &&
		!isModalOpen &&
		fg('platform_editor_ai_remix_in_editor_loading')
	) {
		return !isThinkingWidgetInViewport ? (
			<RemixReadyToast
				isUpdate={fg('platform_editor_ai_remix_toolbar') ? isUpdate : undefined}
				onView={handleReadyToastView}
				onClose={handleReadyToastClose}
			/>
		) : null;
	}

	// When the flag is ON, only show when isModalOpen, isThinking, or showLocationPicker — this
	// is the correct gating behaviour for the new loading path.
	// When the flag is OFF (legacy path), always show — EXCEPT when a pre-supplied userPrompt
	// is present (image-gen flow) and the flow is done (neither isModalOpen nor isThinking),
	// to prevent the option-picker from flashing on cancellation.
	const hasPreSuppliedPrompt = !!remixModalMetadata?.userPrompt;
	const canShow =
		isModalOpen ||
		isThinking ||
		showLocationPicker ||
		(!fg('platform_editor_ai_remix_in_editor_loading') && !hasPreSuppliedPrompt);

	return canShow ? (
		<Component
			isViewMode={fg('platform_editor_ai_remix_toolbar') ? isViewMode : undefined}
			isModalOpen={isModalOpen}
			isThinking={isThinking}
			isUpdate={fg('platform_editor_ai_remix_toolbar') ? isUpdate : undefined}
			historyEntryForUpdate={
				fg('platform_editor_ai_remix_toolbar') ? historyEntryForUpdate : undefined
			}
			dynamicUiSubtypeForUpdate={
				fg('platform_editor_ai_remix_toolbar') ? dynamicUiSubtypeForUpdate : undefined
			}
			isComplete={fg('platform_editor_ai_remix_in_editor_loading') ? isComplete : undefined}
			handleModalClose={handleModalClose}
			remixSource={remixSource}
			selectedContext={selectedContext}
			editorApi={api}
			clearThinkingDecoration={
				fg('platform_editor_ai_remix_in_editor_loading') ? clearThinkingDecoration : undefined
			}
			replaceWhiteboardEmbed={
				fg('platform_editor_ai_remix_in_editor_loading') ? replaceWhiteboardEmbed : undefined
			}
			setThinkingComplete={
				fg('platform_editor_ai_remix_in_editor_loading') ? setThinkingComplete : undefined
			}
			finishRemixThinking={
				fg('platform_editor_ai_remix_in_editor_loading') ? finishRemixThinking : undefined
			}
			onStartThinking={onStartThinking}
			onStopThinking={onStopThinking}
			onRemixThinkingCompleteAnimationFinished={
				fg('platform_editor_ai_remix_toolbar')
					? _onRemixThinkingCompleteAnimationFinished
					: undefined
			}
			showLocationPicker={
				fg('platform_editor_ai_remix_in_editor_loading') ? showLocationPicker : false
			}
			setShowLocationPicker={
				fg('platform_editor_ai_remix_in_editor_loading') ? setShowLocationPicker : () => {}
			}
			addHistoryEntry={fg('platform_editor_ai_remix_toolbar') ? addHistoryEntry : () => {}}
			addHistoryStackEntry={
				fg('platform_editor_ai_remix_toolbar') ? addHistoryStackEntry : () => {}
			}
			onVariantUpdateComplete={
				fg('platform_editor_ai_remix_toolbar') ? onVariantUpdateComplete : undefined
			}
			remixModalMetadata={remixModalMetadata}
			hiddenOptions={config.hiddenOptions}
		/>
	) : null;
};
